export function debug(...args: any) {
  console.debug('[MM Debug]', ...args);
}

type LoggerOptionsType = {
  timestamp?: boolean;
  timezone?: string;
  level?: string;
  loggerName?: string;
  service?: string;
};
type LoggerDataType = {
  timestamp?: string;
  level?: string;
  loggerName?: string;
  service?: string;
};
const levels = ['fatal', 'error', 'warn', 'info', 'debug', 'trace', 'none'];

/**
 * The `Logger` class is a versatile logging utility that allows you to record logs with varying levels of severity, such as trace, debug, info, warn, error, and fatal.
 *
 * ## Options
 * The class constructor accepts an options object of type `LoggerOptionsType` which can include:
 * - `timestamp` (boolean): Whether to include timestamps in the logs. Default is `true`.
 * - `timezone` (string): Desired timezone for the timestamp. Default is `'UTC'`.
 * - `level` (string): The logging level threshold. Logs below this level won't be output. Default is `'debug'`.
 * - `loggerName` (string): An optional identifier for the logger instance.
 *
 * ## Methods
 * - `trace(object: any)`: Records a 'trace' level log.
 * - `debug(object: any)`: Records a 'debug' level log.
 * - `info(object: any)`: Records an 'info' level log.
 * - `warn(object: any)`: Records a 'warn' level log.
 * - `error(object: any)`: Records an 'error' level log.
 * - `fatal(object: any)`: Records a 'fatal' level log.
 * - `formatDate(timezone?: string): string`: Generates a formatted timestamp based on the given timezone.
 * - `output(level: string, args: any)`: Outputs the log data formatted with the given level and additional arguments.
 *
 * ## Example
 * ```javascript
 * const logger = new Logger({
 *   timestamp: true,
 *   timezone: 'America/New_York',
 *   level: 'info',
 *   loggerName: 'AppLogger'
 * });
 *
 * logger.info('Application started');
 * logger.error('An unexpected error occurred');
 * ```
 */
export class Logger {
  private options: LoggerOptionsType;
  private timestamp: string;
  public trace: (object: any) => void;
  public debug: (object: any) => void;
  public info: (object: any) => void;
  public warn: (object: any) => void;
  public error: (object: any) => void;
  public fatal: (object: any) => void;
  private writeln: (object: any) => void;
  private head: (level: string) => {};
  constructor(options: LoggerOptionsType = {}) {
    this.options = Object.create(options);
    this.options.timestamp = this.options.timestamp !== false;
    this.options.level = (this.options.level || 'debug').toLowerCase();
    this.options.service = this.options.service ?? 'voltax-lightning-service';

    this.timestamp = this.formatDate(this.options.timezone);

    this.writeln = (object) => console.debug(JSON.stringify(object));

    this.head = (level) => {
      const data: LoggerDataType = {};
      if (this.options.timestamp) {
        data.timestamp = this.timestamp;
      }
      data.level = level;
      if (this.options.loggerName) {
        data.loggerName = this.options.loggerName;
      }
      if (this.options.service) {
        data.service = this.options.service;
      }
      return data;
    };

    this.trace =
      this.debug =
      this.info =
      this.warn =
      this.error =
      this.fatal =
        () => {};
    const level = levels.includes(this.options.level)
      ? this.options.level
      : 'debug';
    switch (level) {
      case 'trace':
        this.trace = function (...args) {
          this.output('trace', Array.from(args));
        };
      // eslint-disable-next-line no-fallthrough
      case 'debug':
        this.debug = function (...args) {
          this.output('debug', Array.from(args));
        };
      // eslint-disable-next-line no-fallthrough
      case 'info':
        this.info = function (...args) {
          this.output('info', Array.from(args));
        };
      // eslint-disable-next-line no-fallthrough
      case 'warn':
        this.warn = function (...args) {
          this.output('warn', Array.from(args));
        };
      // eslint-disable-next-line no-fallthrough
      case 'error':
        this.error = function (...args) {
          this.output('error', Array.from(args));
        };
      // eslint-disable-next-line no-fallthrough
      case 'fatal':
        this.fatal = function (...args) {
          this.output('fatal', Array.from(args));
        };
    }
  }

  formatDate(timezone?: string): string {
    const date = new Date();

    const opts: Intl.DateTimeFormatOptions = {
      timeZone: timezone ?? 'UTC',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3, // For milliseconds
      hour12: false,
    };

    // Format the date to match the "YYYY-MM-DDTHH:mm:ss.SSS" format
    const formatted = new Intl.DateTimeFormat('en-GB', opts).format(date);

    // Convert to desired format YYYY-MM-DDTHH:mm:ss.SSS[Z]
    return (
      formatted
        .replace(', ', 'T')
        .replace(/(\d{2})\/(\d{2})\/(\d{4})/, '$3-$2-$1') + 'Z'
    );
  }

  output(level: string, args: any) {
    const head = this.head(level);
    const obj: any = {};
    let i = 0;

    const assignValues = (each: any) => {
      if (typeof each === 'string') {
        obj[`message${i == 0 ? '' : i}`] = each;
      } else {
        Object.assign(obj, { [`message${i == 0 ? '' : i}`]: each });
      }
      i++;
    };

    Object.assign(obj, head);
    args.forEach(assignValues);

    this.writeln(obj);
  }
}
